import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, Search, FileText, DollarSign, CheckCircle, XCircle, Clock, 
  Send, Eye, Download, Trash2, Edit, AlertTriangle, PenTool,
  FileSignature, Calendar, TrendingUp, Users, ArrowUpCircle, ArrowDownCircle,
  Receipt, Wallet, Building2
} from 'lucide-react';

// Tab components
import { CaixaTab, FaturamentoTab, OrcamentosTab, ContratosTab, DetalhesOrcamentoDialog, DetalhesContratoDialog, DetalhesFaturaDialog, MovimentacaoDialog, FaturaDialog, OrcamentoDialog, ContratoDialog, AssinaturaDialog, statusColors, statusLabels, categoriaLabels } from '@/components/financeiro';
import InativarContratoDialog from '@/components/financeiro/InativarContratoDialog';
import { FretesTab } from '@/components/financeiro/FretesTab';
import { Truck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FinanceiroPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('caixa');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [orcamentos, setOrcamentos] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [contratosExcluidos, setContratosExcluidos] = useState([]);
  const [faturas, setFaturas] = useState([]);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [materiais, setMateriais] = useState([]);
  const [precosMap, setPrecosMap] = useState({});
  const [fluxoCaixa, setFluxoCaixa] = useState(null);
  
  // Search
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialogs
  const [showOrcamentoDialog, setShowOrcamentoDialog] = useState(false);
  const [showContratoDialog, setShowContratoDialog] = useState(false);
  const [showAssinaturaDialog, setShowAssinaturaDialog] = useState(false);
  const [showFaturaDialog, setShowFaturaDialog] = useState(false);
  const [showMovimentacaoDialog, setShowMovimentacaoDialog] = useState(false);
  const [showDetalhesOrcamentoDialog, setShowDetalhesOrcamentoDialog] = useState(false);
  const [showDetalhesContratoDialog, setShowDetalhesContratoDialog] = useState(false);
  const [showDetalhesFaturaDialog, setShowDetalhesFaturaDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedOrcamento, setSelectedOrcamento] = useState(null);
  const [selectedContrato, setSelectedContrato] = useState(null);
  const [selectedFatura, setSelectedFatura] = useState(null);
  const [showInativarDialog, setShowInativarDialog] = useState(false);
  const [contratoParaInativar, setContratoParaInativar] = useState(null);
  
  // Signature canvas ref
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Form states
  const [orcamentoForm, setOrcamentoForm] = useState({
    cliente_id: '',
    itens: [],
    validade_dias: 30,
    observacoes: '',
    condicoes_pagamento: ''
  });
  
  const [contratoForm, setContratoForm] = useState({
    cliente_id: '',
    tipo: 'mensal',
    valor_mensal: 0,
    servicos: [],
    data_inicio: new Date().toISOString().split('T')[0],
    data_fim: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    indice_reajuste: 'igpm',
    periodicidade_reajuste: 'anual',
    observacoes: '',
    clausulas_especiais: ''
  });

  const [assinaturaForm, setAssinaturaForm] = useState({
    nome_signatario: '',
    cpf_signatario: '',
    email_signatario: '',
    assinatura_base64: ''
  });

  const [faturaForm, setFaturaForm] = useState({
    cliente_id: '',
    contrato_id: '',
    periodo_inicio: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    periodo_fim: new Date().toISOString().split('T')[0],
    itens: [],
    observacoes: ''
  });

  const [movimentacaoForm, setMovimentacaoForm] = useState({
    tipo: 'receita',
    categoria: 'servico',
    descricao: '',
    valor: 0,
    data_movimentacao: new Date().toISOString().split('T')[0],
    cliente_id: '',
    centro_custo: '',
    observacoes: ''
  });

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }, []);

  // Fetch data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [orcRes, contratoRes, faturaRes, clienteRes, materialRes, movRes, fluxoRes] = await Promise.all([
        fetch(`${API_URL}/api/financeiro/orcamentos`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/contratos`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/financeiro/faturas`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/clientes`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/materiais`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/financeiro/movimentacoes`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/financeiro/relatorios/fluxo-caixa?${(() => {
          const hoje = new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
          const [y, m] = hoje.split('-').map(Number);
          const fim = new Date(y, m, 0).getDate();
          return `periodo_inicio=${y}-${String(m).padStart(2, '0')}-01&periodo_fim=${y}-${String(m).padStart(2, '0')}-${String(fim).padStart(2, '0')}`;
        })()}`, { headers: getAuthHeaders() })
      ]);
      
      if (orcRes.ok) setOrcamentos(await orcRes.json());
      if (contratoRes.ok) setContratos(await contratoRes.json());
      
      // Fetch excluídos separadamente
      try {
        const exclRes = await fetch(`${API_URL}/api/contratos?status=excluido`, { headers: getAuthHeaders() });
        if (exclRes.ok) setContratosExcluidos(await exclRes.json());
      } catch (e) { /* ignore */ }
      
      if (faturaRes.ok) setFaturas(await faturaRes.json());
      if (clienteRes.ok) setClientes(await clienteRes.json());
      if (materialRes.ok) setMateriais(await materialRes.json());
      fetch(`${API_URL}/api/financeiro/precos`, { headers: getAuthHeaders() })
        .then(r => r.ok ? r.json() : [])
        .then(list => setPrecosMap(Object.fromEntries(list.filter(p => p.valor_unitario != null).map(p => [p.material_id, p.valor_unitario]))))
        .catch(() => {});
      if (movRes.ok) setMovimentacoes(await movRes.json());
      if (fluxoRes.ok) setFluxoCaixa(await fluxoRes.json());
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // ============= MOVIMENTAÇÕES (RECEITAS/DESPESAS) =============
  
  const handleCreateMovimentacao = async () => {
    if (!movimentacaoForm.descricao || movimentacaoForm.valor <= 0) {
      toast.error('Preencha a descrição e um valor válido');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/financeiro/movimentacoes`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(movimentacaoForm)
      });
      
      if (res.ok) {
        toast.success(`${movimentacaoForm.tipo === 'receita' ? 'Receita' : 'Despesa'} registrada com sucesso`);
        setShowMovimentacaoDialog(false);
        resetMovimentacaoForm();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao registrar movimentação');
      }
    } catch (error) {
      toast.error('Erro ao registrar movimentação');
    }
  };

  const handleDeleteMovimentacao = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta movimentação?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/financeiro/movimentacoes/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Movimentação excluída');
        fetchData();
      } else {
        toast.error('Erro ao excluir movimentação');
      }
    } catch (error) {
      toast.error('Erro ao excluir movimentação');
    }
  };

  const resetMovimentacaoForm = () => {
    setMovimentacaoForm({
      tipo: 'receita',
      categoria: 'servico',
      descricao: '',
      valor: 0,
      data_movimentacao: new Date().toISOString().split('T')[0],
      cliente_id: '',
      centro_custo: '',
      observacoes: ''
    });
  };

  // ============= FATURAS =============
  
  const handleCreateFatura = async () => {
    if (!faturaForm.cliente_id) {
      toast.error('Selecione um cliente');
      return;
    }
    if (faturaForm.itens.length === 0) {
      toast.error('Adicione pelo menos um item');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/financeiro/faturas`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(faturaForm)
      });
      
      if (res.ok) {
        toast.success('Fatura criada com sucesso');
        setShowFaturaDialog(false);
        resetFaturaForm();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao criar fatura');
      }
    } catch (error) {
      toast.error('Erro ao criar fatura');
    }
  };

  const handleMarcarPago = async (faturaId) => {
    try {
      const res = await fetch(`${API_URL}/api/financeiro/faturas/${faturaId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: 'pago' })
      });
      
      if (res.ok) {
        toast.success('Fatura marcada como paga');
        fetchData();
      } else {
        toast.error('Erro ao atualizar fatura');
      }
    } catch (error) {
      toast.error('Erro ao atualizar fatura');
    }
  };

  const resetFaturaForm = () => {
    setFaturaForm({
      cliente_id: '',
      contrato_id: '',
      periodo_inicio: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      periodo_fim: new Date().toISOString().split('T')[0],
      itens: [],
      observacoes: ''
    });
  };

  const addItemFatura = () => {
    setFaturaForm(prev => ({
      ...prev,
      itens: [...prev.itens, { descricao: '', quantidade: 1, valor_unitario: 0 }]
    }));
  };

  const updateItemFatura = (index, field, value) => {
    setFaturaForm(prev => ({
      ...prev,
      itens: prev.itens.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const removeItemFatura = (index) => {
    setFaturaForm(prev => ({
      ...prev,
      itens: prev.itens.filter((_, i) => i !== index)
    }));
  };

  // ============= ORÇAMENTOS =============
  
  const [editingOrcamentoId, setEditingOrcamentoId] = useState(null);

  const handleEditOrcamento = (orc) => {
    setEditingOrcamentoId(orc.id);
    setOrcamentoForm({
      cliente_id: orc.cliente_id,
      itens: (orc.itens || []).map(i => ({ material_id: i.material_id, material_nome: i.material_nome, quantidade: i.quantidade, valor_unitario: i.valor_unitario, desconto_percentual: i.desconto_percentual || 0 })),
      validade_dias: orc.validade_dias || 30,
      observacoes: orc.observacoes || '',
      condicoes_pagamento: orc.condicoes_pagamento || ''
    });
    setShowOrcamentoDialog(true);
  };

  const handleDuplicarOrcamento = (orc) => {
    handleEditOrcamento(orc);
    setEditingOrcamentoId(null);
    toast.info(`Cópia de ${orc.numero_orcamento}: ajuste cliente e valores e clique em Criar Orçamento`);
  };

  const handleDeleteOrcamento = async (orc) => {
    if (!window.confirm(`Excluir o orçamento ${orc.numero_orcamento} de ${orc.cliente_nome}?\n\nEsta ação não pode ser desfeita.`)) return;
    const res = await fetch(`${API_URL}/api/financeiro/orcamentos/${orc.id}`, { method: 'DELETE', headers: getAuthHeaders() });
    const data = await res.json().catch(() => ({}));
    if (res.ok) { toast.success('Orçamento excluído'); fetchData(); }
    else toast.error(data.detail || 'Erro ao excluir orçamento');
  };

  const handleCreateOrcamento = async () => {
    if (!orcamentoForm.cliente_id) {
      toast.error('Selecione um cliente');
      return;
    }
    if (orcamentoForm.itens.length === 0) {
      toast.error('Adicione pelo menos um item');
      return;
    }
    if (orcamentoForm.itens.some(i => !i.material_id)) {
      toast.error('Selecione o material de todos os itens');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/financeiro/orcamentos${editingOrcamentoId ? `/${editingOrcamentoId}` : ''}`, {
        method: editingOrcamentoId ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(orcamentoForm)
      });
      
      if (res.ok) {
        toast.success(editingOrcamentoId ? 'Orçamento atualizado' : 'Orçamento criado com sucesso');
        setShowOrcamentoDialog(false);
        resetOrcamentoForm();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao criar orçamento');
      }
    } catch (error) {
      toast.error('Erro ao criar orçamento');
    }
  };

  const handleEnviarOrcamento = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/financeiro/orcamentos/${id}/enviar`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Orçamento enviado');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao enviar orçamento');
      }
    } catch (error) {
      toast.error('Erro ao enviar orçamento');
    }
  };

  const handleAprovarOrcamento = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/financeiro/orcamentos/${id}/aprovar`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Orçamento aprovado');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao aprovar orçamento');
      }
    } catch (error) {
      toast.error('Erro ao aprovar orçamento');
    }
  };

  const resetOrcamentoForm = () => {
    setEditingOrcamentoId(null);
    setOrcamentoForm({
      cliente_id: '',
      itens: [],
      validade_dias: 30,
      observacoes: '',
      condicoes_pagamento: ''
    });
  };

  const addItemOrcamento = () => {
    setOrcamentoForm(prev => ({
      ...prev,
      itens: [...prev.itens, { material_id: '', quantidade: 1, valor_unitario: 0, desconto_percentual: 0 }]
    }));
  };

  const updateItemOrcamento = (index, field, value) => {
    setOrcamentoForm(prev => ({
      ...prev,
      itens: prev.itens.map((item, i) => i === index
        ? { ...item, [field]: value, ...(field === 'material_id' ? { material_nome: materiais.find(m => m.id === value)?.nome, valor_unitario: precosMap[value] ?? item.valor_unitario } : {}) }
        : item)
    }));
  };

  const handleEnviarWhatsappOrcamento = async (orc) => {
    if (!window.confirm(`Enviar o orçamento ${orc.numero_orcamento} em PDF para o WhatsApp de ${orc.cliente_nome}?`)) return;
    const t = toast.loading('Enviando orçamento pelo WhatsApp...');
    try {
      const res = await fetch(`${API_URL}/api/financeiro/orcamentos/${orc.id}/whatsapp`, { method: 'POST', headers: getAuthHeaders() });
      const data = await res.json().catch(() => ({}));
      toast.dismiss(t);
      if (res.ok) { toast.success(data.message); fetchData(); }
      else toast.error(data.detail || 'Erro ao enviar pelo WhatsApp');
    } catch {
      toast.dismiss(t);
      toast.error('Erro ao enviar pelo WhatsApp');
    }
  };

  const removeItemOrcamento = (index) => {
    setOrcamentoForm(prev => ({
      ...prev,
      itens: prev.itens.filter((_, i) => i !== index)
    }));
  };

  // ============= CONTRATOS =============
  
  const handleCreateContrato = async () => {
    if (!contratoForm.cliente_id) {
      toast.error('Selecione um cliente');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/contratos`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(contratoForm)
      });
      
      if (res.ok) {
        toast.success('Contrato criado com sucesso');
        setShowContratoDialog(false);
        resetContratoForm();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao criar contrato');
      }
    } catch (error) {
      toast.error('Erro ao criar contrato');
    }
  };

  const handleEnviarParaAssinatura = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/contratos/${id}/enviar-para-assinatura`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Contrato enviado para assinatura');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao enviar contrato');
      }
    } catch (error) {
      toast.error('Erro ao enviar contrato');
    }
  };

  const handleAssinarContrato = async () => {
    if (!assinaturaForm.nome_signatario) {
      toast.error('Nome do signatário é obrigatório');
      return;
    }
    
    const canvas = canvasRef.current;
    const assinatura_base64 = canvas ? canvas.toDataURL('image/png') : '';
    
    try {
      const res = await fetch(`${API_URL}/api/contratos/${selectedItem.id}/assinar`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...assinaturaForm,
          assinatura_base64
        })
      });
      
      if (res.ok) {
        toast.success('Contrato assinado com sucesso!');
        setShowAssinaturaDialog(false);
        setSelectedItem(null);
        resetAssinaturaForm();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao assinar contrato');
      }
    } catch (error) {
      toast.error('Erro ao assinar contrato');
    }
  };

  const handleDownloadPDF = async (id, tipo) => {
    try {
      let endpoint;
      if (tipo === 'contrato') {
        endpoint = `${API_URL}/api/contratos/${id}/pdf`;
      } else if (tipo === 'orcamento') {
        endpoint = `${API_URL}/api/financeiro/orcamentos/${id}/pdf`;
      } else {
        endpoint = `${API_URL}/api/ciclos/${id}/pdf`;
      }
      
      const res = await fetch(endpoint, { headers: getAuthHeaders() });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${tipo}-${id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('PDF baixado');
      } else {
        toast.error('Erro ao baixar PDF');
      }
    } catch (error) {
      toast.error('Erro ao baixar PDF');
    }
  };

  const handleVerDetalhesOrcamento = (orc) => {
    setSelectedOrcamento(orc);
    setShowDetalhesOrcamentoDialog(true);
  };

  const handleVerDetalhesContrato = (contrato) => {
    setSelectedContrato(contrato);
    setShowDetalhesContratoDialog(true);
  };

  const handleInativarContrato = async (contrato) => {
    setContratoParaInativar(contrato);
    setShowInativarDialog(true);
  };

  const handleConfirmarInativacao = async (contrato, motivo) => {
    try {
      const res = await fetch(`${API_URL}/api/contratos/${contrato.id}/inativar`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ motivo })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        if (data.notificacoes?.whatsapp_enviado) {
          toast.success('WhatsApp de bloqueio enviado ao cliente');
        }
        if (data.notificacoes?.email_enviado) {
          toast.success('Email de bloqueio enviado ao cliente');
        }
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao inativar contrato');
      }
    } catch (error) {
      toast.error('Erro ao inativar contrato');
    }
  };

  const handleReativarContrato = async (contratoId) => {
    try {
      const res = await fetch(`${API_URL}/api/contratos/${contratoId}/reativar`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao reativar contrato');
      }
    } catch (error) {
      toast.error('Erro ao reativar contrato');
    }
  };

  const handleExcluirPDF = async (contrato) => {
    if (!window.confirm(`Deseja excluir o PDF/assinatura do contrato ${contrato.numero_contrato}? Esta ação não pode ser desfeita.`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/contratos/${contrato.id}/pdf`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao excluir PDF');
      }
    } catch (error) {
      toast.error('Erro ao excluir PDF');
    }
  };

  const handleExcluirContrato = async (contrato) => {
    if (!window.confirm(`Deseja excluir o contrato ${contrato.numero_contrato}? Ele será movido para a aba "Excluídos" e poderá ser restaurado depois.`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/contratos/${contrato.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao excluir contrato');
      }
    } catch (error) {
      toast.error('Erro ao excluir contrato');
    }
  };

  const handleRestaurarContrato = async (contratoId) => {
    try {
      const res = await fetch(`${API_URL}/api/contratos/${contratoId}/restaurar`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao restaurar contrato');
      }
    } catch (error) {
      toast.error('Erro ao restaurar contrato');
    }
  };

  const handleVerDetalhesFatura = (fatura) => {
    setSelectedFatura(fatura);
    setShowDetalhesFaturaDialog(true);
  };

  const resetContratoForm = () => {
    setContratoForm({
      cliente_id: '',
      tipo: 'mensal',
      valor_mensal: 0,
      servicos: [],
      data_inicio: new Date().toISOString().split('T')[0],
      data_fim: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      indice_reajuste: 'igpm',
      periodicidade_reajuste: 'anual',
      observacoes: '',
      clausulas_especiais: ''
    });
  };

  const resetAssinaturaForm = () => {
    setAssinaturaForm({
      nome_signatario: '',
      cpf_signatario: '',
      email_signatario: '',
      assinatura_base64: ''
    });
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const addServicoContrato = () => {
    setContratoForm(prev => ({
      ...prev,
      servicos: [...prev.servicos, { descricao: '', valor_unitario: 0, quantidade_inclusa: 0, valor_excedente: 0 }]
    }));
  };

  const updateServicoContrato = (index, field, value) => {
    setContratoForm(prev => ({
      ...prev,
      servicos: prev.servicos.map((s, i) => i === index ? { ...s, [field]: value } : s)
    }));
  };

  const removeServicoContrato = (index) => {
    setContratoForm(prev => ({
      ...prev,
      servicos: prev.servicos.filter((_, i) => i !== index)
    }));
  };

  // ============= CANVAS ASSINATURA =============
  
  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#1e40af';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  };

  useEffect(() => {
    if (showAssinaturaDialog) {
      setTimeout(initCanvas, 100);
    }
  }, [showAssinaturaDialog]);

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  // ============= FILTERING =============
  
  const filteredOrcamentos = orcamentos.filter(o => 
    o.numero_orcamento?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.cliente_nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredContratos = contratos.filter(c => 
    c.numero_contrato?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.cliente_nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFaturas = faturas.filter(f => 
    f.numero_fatura?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.cliente_nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredMovimentacoes = movimentacoes.filter(m =>
    m.descricao?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.categoria?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ============= STATS =============
  
  const stats = {
    totalReceitas: fluxoCaixa?.total_receitas ?? movimentacoes.filter(m => m.tipo === 'receita').reduce((acc, m) => acc + (m.valor || 0), 0),
    totalDespesas: fluxoCaixa?.total_despesas ?? movimentacoes.filter(m => m.tipo === 'despesa').reduce((acc, m) => acc + (m.valor || 0), 0),
    saldo: fluxoCaixa?.saldo ?? 0,
    totalContratos: contratos.length,
    contratosAtivos: contratos.filter(c => c.status === 'ativo').length,
    totalFaturas: faturas.length,
    faturasPendentes: faturas.filter(f => f.status === 'pendente').length,
    valorFaturasPendentes: faturas.filter(f => f.status === 'pendente').reduce((acc, f) => acc + (f.valor_total || 0), 0)
  };

  return (
    <div className="space-y-6" data-testid="financeiro-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Financeiro</h1>
          <p className="text-slate-500">Receitas, despesas, orçamentos, contratos e faturamento</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64"
              data-testid="search-input"
            />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <ArrowUpCircle className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-sm text-green-600 font-medium">Receitas (Mês)</p>
                <p className="text-xl font-bold text-green-900">
                  R$ {stats.totalReceitas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <ArrowDownCircle className="w-8 h-8 text-red-600" />
              <div>
                <p className="text-sm text-red-600 font-medium">Despesas (Mês)</p>
                <p className="text-xl font-bold text-red-900">
                  R$ {stats.totalDespesas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`bg-gradient-to-br ${stats.saldo >= 0 ? 'from-blue-50 to-blue-100 border-blue-200' : 'from-orange-50 to-orange-100 border-orange-200'}`}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Wallet className={`w-8 h-8 ${stats.saldo >= 0 ? 'text-blue-600' : 'text-orange-600'}`} />
              <div>
                <p className={`text-sm font-medium ${stats.saldo >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Saldo (Mês)</p>
                <p className={`text-xl font-bold ${stats.saldo >= 0 ? 'text-blue-900' : 'text-orange-900'}`}>
                  R$ {stats.saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Receipt className="w-8 h-8 text-amber-600" />
              <div>
                <p className="text-sm text-amber-600 font-medium">A Receber</p>
                <p className="text-xl font-bold text-amber-900">
                  R$ {stats.valorFaturasPendentes.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-amber-600">{stats.faturasPendentes} fatura(s)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="caixa" data-testid="tab-caixa">
            <Wallet className="w-4 h-4 mr-2" />
            Caixa
          </TabsTrigger>
          <TabsTrigger value="faturamento" data-testid="tab-faturamento">
            <Receipt className="w-4 h-4 mr-2" />
            Faturamento
          </TabsTrigger>
          <TabsTrigger value="orcamentos" data-testid="tab-orcamentos">
            <FileText className="w-4 h-4 mr-2" />
            Orçamentos
          </TabsTrigger>
          <TabsTrigger value="contratos" data-testid="tab-contratos">
            <FileSignature className="w-4 h-4 mr-2" />
            Contratos
          </TabsTrigger>
          <TabsTrigger value="fretes" data-testid="tab-fretes">
            <Truck className="w-4 h-4 mr-2" />
            Fretes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="fretes" className="space-y-4">
          <FretesTab onChanged={fetchData} />
        </TabsContent>

        {/* ============= CAIXA (RECEITAS/DESPESAS) TAB ============= */}
        <TabsContent value="caixa" className="space-y-4">
          <CaixaTab 
            movimentacoes={filteredMovimentacoes}
            onNovaReceita={() => { setMovimentacaoForm(prev => ({ ...prev, tipo: 'receita' })); setShowMovimentacaoDialog(true); }}
            onNovaDespesa={() => { setMovimentacaoForm(prev => ({ ...prev, tipo: 'despesa' })); setShowMovimentacaoDialog(true); }}
            onDelete={handleDeleteMovimentacao}
          />
        </TabsContent>

        {/* ============= FATURAMENTO TAB ============= */}
        <TabsContent value="faturamento" className="space-y-4">
          <FaturamentoTab 
            faturas={filteredFaturas}
            onNovaFatura={() => setShowFaturaDialog(true)}
            onMarcarPago={handleMarcarPago}
            onVerDetalhes={handleVerDetalhesFatura}
          />
        </TabsContent>

        {/* ============= ORÇAMENTOS TAB ============= */}
        <TabsContent value="orcamentos" className="space-y-4">
          <OrcamentosTab 
            orcamentos={filteredOrcamentos}
            onNovoOrcamento={() => setShowOrcamentoDialog(true)}
            onEnviar={handleEnviarOrcamento}
            onAprovar={handleAprovarOrcamento}
            onDownloadPDF={(id) => handleDownloadPDF(id, 'orcamento')}
            onVerDetalhes={handleVerDetalhesOrcamento}
            onEditar={handleEditOrcamento}
            onExcluir={handleDeleteOrcamento}
            onWhatsapp={handleEnviarWhatsappOrcamento}
            onDuplicar={handleDuplicarOrcamento}
          />
        </TabsContent>

        {/* ============= CONTRATOS TAB ============= */}
        <TabsContent value="contratos" className="space-y-4">
          <ContratosTab 
            contratos={filteredContratos}
            contratosExcluidos={contratosExcluidos}
            onNovoContrato={() => setShowContratoDialog(true)}
            onEnviarParaAssinatura={handleEnviarParaAssinatura}
            onAssinar={(contrato) => { setSelectedItem(contrato); setShowAssinaturaDialog(true); }}
            onDownloadPDF={(id) => handleDownloadPDF(id, 'contrato')}
            onVerDetalhes={handleVerDetalhesContrato}
            onInativar={handleInativarContrato}
            onReativar={handleReativarContrato}
            onExcluirPDF={handleExcluirPDF}
            onExcluir={handleExcluirContrato}
            onRestaurar={handleRestaurarContrato}
          />
        </TabsContent>
      </Tabs>

      {/* ============= DIALOG NOVA MOVIMENTAÇÃO ============= */}
      <MovimentacaoDialog
        open={showMovimentacaoDialog}
        onOpenChange={setShowMovimentacaoDialog}
        form={movimentacaoForm}
        setForm={setMovimentacaoForm}
        onSubmit={handleCreateMovimentacao}
        onReset={resetMovimentacaoForm}
        clientes={clientes}
      />

      {/* ============= DIALOG NOVA FATURA ============= */}
      <FaturaDialog
        open={showFaturaDialog}
        onOpenChange={setShowFaturaDialog}
        form={faturaForm}
        setForm={setFaturaForm}
        onSubmit={handleCreateFatura}
        onReset={resetFaturaForm}
        clientes={clientes}
        contratos={contratos}
        onAddItem={addItemFatura}
        onUpdateItem={updateItemFatura}
        onRemoveItem={removeItemFatura}
      />

      {/* ============= DIALOG NOVO ORÇAMENTO ============= */}
      <OrcamentoDialog
        open={showOrcamentoDialog}
        onOpenChange={setShowOrcamentoDialog}
        form={orcamentoForm}
        setForm={setOrcamentoForm}
        onSubmit={handleCreateOrcamento}
        onReset={resetOrcamentoForm}
        clientes={clientes}
        materiais={materiais}
        onAddItem={addItemOrcamento}
        onUpdateItem={updateItemOrcamento}
        onRemoveItem={removeItemOrcamento}
        editing={!!editingOrcamentoId}
      />

      {/* ============= DIALOG NOVO CONTRATO ============= */}
      <ContratoDialog
        open={showContratoDialog}
        onOpenChange={setShowContratoDialog}
        form={contratoForm}
        setForm={setContratoForm}
        onSubmit={handleCreateContrato}
        onReset={resetContratoForm}
        clientes={clientes}
        onAddServico={addServicoContrato}
        onUpdateServico={updateServicoContrato}
        onRemoveServico={removeServicoContrato}
      />

      {/* ============= DIALOG ASSINATURA ============= */}
      <AssinaturaDialog
        open={showAssinaturaDialog}
        onOpenChange={setShowAssinaturaDialog}
        form={assinaturaForm}
        setForm={setAssinaturaForm}
        onSubmit={handleAssinarContrato}
        onReset={resetAssinaturaForm}
        contrato={selectedItem}
        canvasRef={canvasRef}
        isDrawing={isDrawing}
        onStartDrawing={startDrawing}
        onDraw={draw}
        onStopDrawing={stopDrawing}
        onClearCanvas={clearCanvas}
      />

      {/* ============= DIALOGS DE DETALHES (Componentes) ============= */}
      <DetalhesOrcamentoDialog 
        open={showDetalhesOrcamentoDialog}
        onOpenChange={setShowDetalhesOrcamentoDialog}
        orcamento={selectedOrcamento}
        onDownloadPDF={(id) => handleDownloadPDF(id, 'orcamento')}
      />
      
      <DetalhesContratoDialog 
        open={showDetalhesContratoDialog}
        onOpenChange={setShowDetalhesContratoDialog}
        contrato={selectedContrato}
        onDownloadPDF={(id) => handleDownloadPDF(id, 'contrato')}
      />
      
      <DetalhesFaturaDialog 
        open={showDetalhesFaturaDialog}
        onOpenChange={setShowDetalhesFaturaDialog}
        fatura={selectedFatura}
        onMarcarPago={handleMarcarPago}
      />

      <InativarContratoDialog
        open={showInativarDialog}
        onClose={() => { setShowInativarDialog(false); setContratoParaInativar(null); }}
        contrato={contratoParaInativar}
        onConfirm={handleConfirmarInativacao}
      />
    </div>
  );
}
